import axios from 'axios';
import { 
  Employee,
  SalaryProfile,
  PayrollMonth,
  PayrollItem,
  TaxBracket,
  JobTitle,
  PayrollSummary,
  EmployeeFilters,
  PayrollMonthFilters,
  ApiResponse
} from '../types/payroll';
import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';
import { loginRequest, protectedResources, msalConfig, developmentConfig } from '../config/authConfig';

// Initialize MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

// Create axios instance with base URL
const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const apiClient = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Debug logging function
const debugToken = (message: string, data: any = null) => {
  console.log(`[PAYROLL API DEBUG] ${message}`);
  if (data) {
    // Safely log token info without exposing full token
    if (data.accessToken) {
      console.log('Token info:', {
        tokenStart: data.accessToken.substring(0, 10) + '...',
        tokenLength: data.accessToken.length,
        tokenType: typeof data.accessToken,
        scopes: data.scopes,
        expiresOn: data.expiresOn,
        account: data.account ? {
          homeAccountId: data.account.homeAccountId,
          environment: data.account.environment,
          tenantId: data.account.tenantId,
          username: data.account.username
        } : 'none'
      });
    } else {
      console.log(data);
    }
  }
};

// Add auth interceptor to add token to requests
apiClient.interceptors.request.use(
  async (config) => {
    debugToken('API request interceptor called', { url: config.url });
    
    // In development mode with mock authentication enabled
    if (developmentConfig.enabled) {
      debugToken('Using mock authentication with development token', { 
        enabled: developmentConfig.enabled,
        tokenLength: developmentConfig.developmentToken.length 
      });
      // Add the development token
      config.headers['Authorization'] = `Bearer ${developmentConfig.developmentToken}`;
      return config;
    }

    // Handle real Microsoft authentication
    debugToken('Using Microsoft authentication');
    let activeAccount = msalInstance.getActiveAccount();
    
    // If no active account is set but we have accounts, set the first one as active
    if (!activeAccount && msalInstance.getAllAccounts().length > 0) {
      debugToken('No active account set but accounts exist, setting first account as active');
      const accounts = msalInstance.getAllAccounts();
      msalInstance.setActiveAccount(accounts[0]);
      activeAccount = accounts[0];
    }
    
    if (activeAccount) {
      debugToken('Active account found', { 
        username: activeAccount.username,
        tenantId: activeAccount.tenantId,
        environment: activeAccount.environment,
        localAccountId: activeAccount.localAccountId
      });
      
      try {
        debugToken('Attempting to acquire token silently with scopes:', loginRequest.scopes);
        // Try to acquire token silently first, with explicit scopes
        const response = await msalInstance.acquireTokenSilent({
          scopes: loginRequest.scopes,
          account: activeAccount,
          forceRefresh: false // Set to true if you want to force a token refresh
        });
        
        debugToken('Token acquired successfully', {
          accessToken: response.accessToken ? `${response.accessToken.substring(0, 10)}...` : 'none',
          tokenLength: response.accessToken ? response.accessToken.length : 0,
          scopes: response.scopes,
          account: response.account ? response.account.username : 'none',
          expiresOn: response.expiresOn
        });
        
        // Make sure we're setting the token correctly
        if (response.accessToken) {
          config.headers['Authorization'] = `Bearer ${response.accessToken}`;
          debugToken('Authorization header set successfully');
          
          // Verify that the token is properly formatted
          const tokenParts = response.accessToken.split('.');
          if (tokenParts.length !== 3) {
            console.warn('Token does not appear to be in valid JWT format');
          } else {
            // Log token claims for debugging (without exposing sensitive data)
            try {
              const tokenPayload = JSON.parse(atob(tokenParts[1]));
              console.log('Token claims:', {
                aud: tokenPayload.aud,
                iss: tokenPayload.iss?.substring(0, 20) + '...',
                scp: tokenPayload.scp,
                exp: new Date(tokenPayload.exp * 1000).toISOString(),
                nbf: new Date(tokenPayload.nbf * 1000).toISOString(),
                roles: tokenPayload.roles || 'none'
              });
              
              // Check if token has the required scope
              if (!tokenPayload.scp || !tokenPayload.scp.includes('access_as_user')) {
                console.warn('Token does not have the required access_as_user scope');
              }
            } catch (e) {
              console.error('Error parsing token payload:', e);
            }
          }
        } else {
          debugToken('No access token received from MSAL');
        }
      } catch (error) {
        // If silent token acquisition fails, the user might need to login again
        if (error instanceof InteractionRequiredAuthError) {
          debugToken('Interaction required for authentication', { errorType: 'InteractionRequiredAuthError' });
          // Redirect to login page
          window.location.href = '/login';
        } else {
          debugToken('Error acquiring token', { error: error });
          console.error('Error acquiring token:', error);
        }
      }
    } else {
      console.warn('No active account found, user may not be authenticated');
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Employee API Service
export const EmployeeService = {
  // Get all employees
  getAllEmployees: async (filters?: EmployeeFilters): Promise<ApiResponse<Employee[]>> => {
    let url = '/v1/payroll/employees';
    
    // Add query parameters for filters
    if (filters) {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.jobTitleId) params.append('jobTitleId', filters.jobTitleId.toString());
      if (filters.includeInactive) params.append('includeInactive', filters.includeInactive.toString());
      if (filters.withSalary) params.append('withSalary', filters.withSalary.toString());
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
    }
    
    const response = await apiClient.get(url);
    return response.data;
  },

  // Get employee by ID
  getEmployeeById: async (id: number): Promise<ApiResponse<Employee>> => {
    const response = await apiClient.get(`/v1/payroll/employees/${id}`);
    return response.data;
  },

  // Create new employee
  createEmployee: async (employee: Partial<Employee>): Promise<ApiResponse<Employee>> => {
    const response = await apiClient.post('/v1/payroll/employees', employee);
    return response.data;
  },

  // Update employee
  updateEmployee: async (id: number, employee: Partial<Employee>): Promise<ApiResponse<Employee>> => {
    const response = await apiClient.put(`/v1/payroll/employees/${id}`, employee);
    return response.data;
  },

  // Delete employee
  deleteEmployee: async (id: number): Promise<ApiResponse<Employee>> => {
    const response = await apiClient.delete(`/v1/payroll/employees/${id}`);
    return response.data;
  },

  // Get all job titles
  getJobTitles: async (): Promise<ApiResponse<JobTitle[]>> => {
    const response = await apiClient.get('/v1/payroll/job-titles');
    return response.data;
  },

  // Create new job title
  createJobTitle: async (jobTitle: Partial<JobTitle>): Promise<ApiResponse<JobTitle>> => {
    const response = await apiClient.post('/v1/payroll/job-titles', jobTitle);
    return response.data;
  },

  // Import employees from file
  importEmployees: async (formData: FormData): Promise<any> => {
    const response = await apiClient.post('/v1/payroll/employees/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  // Preview import file
  previewImport: async (formData: FormData): Promise<any> => {
    const response = await apiClient.post('/v1/payroll/employees/import/preview', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  // Download import template
  downloadImportTemplate: async (): Promise<any> => {
    const response = await apiClient.get('/v1/payroll/employees/import/template', {
      responseType: 'blob'
    });
    return response;
  }
};

// Salary Profile API Service
export const SalaryProfileService = {
  // Get all salary profiles for an employee
  getAllForEmployee: async (employeeId: number): Promise<ApiResponse<SalaryProfile[]>> => {
    const response = await apiClient.get(`/v1/payroll/employees/${employeeId}/salary-profiles`);
    return response.data;
  },

  // Get current active salary profile for an employee
  getCurrentForEmployee: async (employeeId: number): Promise<ApiResponse<SalaryProfile>> => {
    const response = await apiClient.get(`/v1/payroll/employees/${employeeId}/current-salary`);
    return response.data;
  },

  // Get salary profile by ID
  getSalaryProfileById: async (id: number): Promise<ApiResponse<SalaryProfile>> => {
    const response = await apiClient.get(`/v1/payroll/salary-profiles/${id}`);
    return response.data;
  },

  // Create new salary profile
  createSalaryProfile: async (salaryProfile: Partial<SalaryProfile>): Promise<ApiResponse<SalaryProfile>> => {
    const response = await apiClient.post('/v1/payroll/salary-profiles', salaryProfile);
    return response.data;
  },

  // Update salary profile
  updateSalaryProfile: async (id: number, salaryProfile: Partial<SalaryProfile>): Promise<ApiResponse<SalaryProfile>> => {
    const response = await apiClient.put(`/v1/payroll/salary-profiles/${id}`, salaryProfile);
    return response.data;
  },

  // Delete salary profile
  deleteSalaryProfile: async (id: number): Promise<ApiResponse<SalaryProfile>> => {
    const response = await apiClient.delete(`/v1/payroll/salary-profiles/${id}`);
    return response.data;
  }
};

// Payroll Month API Service
export const PayrollMonthService = {
  // Get all payroll months
  getAllPayrollMonths: async (filters?: PayrollMonthFilters): Promise<ApiResponse<PayrollMonth[]>> => {
    let url = '/v1/payroll/months';
    
    // Add query parameters for filters
    if (filters && filters.status) {
      url += `?status=${filters.status}`;
    }
    
    const response = await apiClient.get(url);
    return response.data;
  },

  // Get payroll month by ID
  getPayrollMonthById: async (id: number): Promise<ApiResponse<PayrollMonth>> => {
    const response = await apiClient.get(`/v1/payroll/months/${id}`);
    return response.data;
  },

  // Get payroll month by period (YYYY-MM)
  getPayrollMonthByPeriod: async (period: string): Promise<ApiResponse<PayrollMonth>> => {
    const response = await apiClient.get(`/v1/payroll/months/period/${period}`);
    return response.data;
  },

  // Create new payroll month
  createPayrollMonth: async (payrollMonth: Partial<PayrollMonth>): Promise<ApiResponse<PayrollMonth>> => {
    const response = await apiClient.post('/v1/payroll/months', payrollMonth);
    return response.data;
  },

  // Update payroll month
  updatePayrollMonth: async (id: number, payrollMonth: Partial<PayrollMonth>): Promise<ApiResponse<PayrollMonth>> => {
    const response = await apiClient.put(`/v1/payroll/months/${id}`, payrollMonth);
    return response.data;
  },

  // Update payroll month status
  updatePayrollMonthStatus: async (id: number, status: string): Promise<ApiResponse<PayrollMonth>> => {
    const response = await apiClient.patch(`/v1/payroll/months/${id}/status`, { status });
    return response.data;
  },

  // Delete payroll month
  deletePayrollMonth: async (id: number): Promise<ApiResponse<PayrollMonth>> => {
    const response = await apiClient.delete(`/v1/payroll/months/${id}`);
    return response.data;
  },

  // Submit payroll month for approval
  submitPayrollMonth: async (id: number): Promise<ApiResponse<PayrollMonth>> => {
    const response = await apiClient.post(`/v1/payroll/months/${id}/submit`);
    return response.data;
  },

  // Approve payroll month
  approvePayrollMonth: async (id: number): Promise<ApiResponse<PayrollMonth>> => {
    const response = await apiClient.post(`/v1/payroll/months/${id}/approve`);
    return response.data;
  },

  // Revert payroll month to draft status
  revertToDraft: async (id: number): Promise<ApiResponse<PayrollMonth>> => {
    const response = await apiClient.post(`/v1/payroll/months/${id}/revert`);
    return response.data;
  },

  // Get summary statistics for a payroll month
  getPayrollSummary: async (id: number): Promise<ApiResponse<PayrollSummary>> => {
    const response = await apiClient.get(`/v1/payroll/months/${id}/summary`);
    return response.data;
  }
};

// Payroll Item API Service
export const PayrollItemService = {
  // Get all payroll items for a month
  getAllForMonth: async (payrollMonthId: number): Promise<ApiResponse<PayrollItem[]>> => {
    const response = await apiClient.get(`/v1/payroll/months/${payrollMonthId}/items`);
    return response.data;
  },

  // Get payroll item by ID
  getPayrollItemById: async (id: number): Promise<ApiResponse<PayrollItem>> => {
    const response = await apiClient.get(`/v1/payroll/items/${id}`);
    return response.data;
  },

  // Get all payroll items for an employee
  getPayrollItemsForEmployee: async (employeeId: number): Promise<ApiResponse<PayrollItem[]>> => {
    const response = await apiClient.get(`/v1/payroll/employees/${employeeId}/items`);
    return response.data;
  },

  // Create a new payroll item
  createPayrollItem: async (payrollItem: Partial<PayrollItem>): Promise<ApiResponse<PayrollItem>> => {
    const response = await apiClient.post('/v1/payroll/items', payrollItem);
    return response.data;
  },

  // Update a payroll item
  updatePayrollItem: async (id: number, payrollItem: Partial<PayrollItem>): Promise<ApiResponse<PayrollItem>> => {
    const response = await apiClient.put(`/v1/payroll/items/${id}`, payrollItem);
    return response.data;
  },

  // Add employee to payroll
  addEmployeeToPayroll: async (payrollMonthId: number, employeeId: number): Promise<ApiResponse<PayrollItem>> => {
    const response = await apiClient.post(`/v1/payroll/months/${payrollMonthId}/employees/${employeeId}`);
    return response.data;
  },

  // Remove employee from payroll
  removeEmployeeFromPayroll: async (payrollItemId: number): Promise<ApiResponse<PayrollItem>> => {
    const response = await apiClient.delete(`/api/v1/payroll/items/${payrollItemId}`);
    return response.data;
  },

  // Calculate a single payroll item
  calculatePayrollItem: async (id: number): Promise<ApiResponse<PayrollItem>> => {
    const response = await apiClient.post(`/api/v1/payroll/items/${id}/calculate`);
    return response.data;
  },

  // Calculate all payroll items for a month
  calculateAllForMonth: async (payrollMonthId: number): Promise<ApiResponse<PayrollItem[]>> => {
    const response = await apiClient.post(`/api/v1/payroll/months/${payrollMonthId}/calculate`);
    return response.data;
  }
};

// Tax Bracket API Service
export const TaxBracketService = {
  // Get all tax brackets
  getAllTaxBrackets: async (): Promise<ApiResponse<TaxBracket[]>> => {
    const response = await apiClient.get('/api/v1/payroll/tax-brackets');
    return response.data;
  },

  // Get current effective tax brackets
  getCurrentBrackets: async (): Promise<ApiResponse<TaxBracket[]>> => {
    const response = await apiClient.get('/api/v1/payroll/tax-brackets/current');
    return response.data;
  },

  // Get tax brackets for a specific date
  getBracketsForDate: async (date: string): Promise<ApiResponse<TaxBracket[]>> => {
    const response = await apiClient.get(`/api/v1/payroll/tax-brackets/date/${date}`);
    return response.data;
  },

  // Get tax bracket by ID
  getTaxBracketById: async (id: number): Promise<ApiResponse<TaxBracket>> => {
    const response = await apiClient.get(`/api/v1/payroll/tax-brackets/${id}`);
    return response.data;
  },

  // Create new tax bracket
  createTaxBracket: async (taxBracket: Partial<TaxBracket>): Promise<ApiResponse<TaxBracket>> => {
    const response = await apiClient.post('/api/v1/payroll/tax-brackets', taxBracket);
    return response.data;
  },

  // Update tax bracket
  updateTaxBracket: async (id: number, taxBracket: Partial<TaxBracket>): Promise<ApiResponse<TaxBracket>> => {
    const response = await apiClient.put(`/api/v1/payroll/tax-brackets/${id}`, taxBracket);
    return response.data;
  },

  // Delete tax bracket
  deleteTaxBracket: async (id: number): Promise<ApiResponse<TaxBracket>> => {
    const response = await apiClient.delete(`/api/v1/payroll/tax-brackets/${id}`);
    return response.data;
  }
};
